import React, { useMemo } from "react";

import { Availability, Section } from "../types/catalog";
import { CategoryListing } from "./category_listing";
import { useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import * as CM from "src/storefront/types/cart";

export const SectionListing = ({
  section,
  useSectionTitles,
  addToCart,
  removeFromCart,
  updateCart
}: {
  section: Section;
  useSectionTitles: boolean;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
}): React.JSX.Element => {
  const config = useStorefrontConfig();
  const { catalog } = useStorefrontHelper();

  const availabilityLabel = useMemo(() => {
    const preferred = config.preferredTime;
    const wday = preferred != null ? preferred.dayOfWeek % 7 : -1;

    if (catalog.isAvailableForPreferredTime(config, section)) {
      return "";
    }

    if (config.presentationStatus == 'closed_for_today') {
      return `Unavailable today`
    }

    const todaysAvailabilities = section.availabilities.filter(av => av.wday === wday)

    if (todaysAvailabilities.length === 0) {
      return `Unavailable today`;
    }

    const descriptors = todaysAvailabilities
      .map((av) => {
        const offset_start = av.startTime.add({ minutes: config.timeOffset })
        const offset_end = av.endTime.add({ minutes: config.timeOffset })

        return new Availability(offset_start, offset_end, av.wday)
      })
      .map((av) => {
        const s_str:string = av.startTime.toLocaleString("en-NZ", {
          timeStyle: "short"
        });

        const e_str:string = av.endTime.toLocaleString("en-NZ", {
          timeStyle: "short"
        })

        const full_start_time = s_str.toUpperCase()
        const full_end_time = e_str.toUpperCase()

        return { full_start_time, full_end_time }
      })

    const timeString = descriptors.map((desc) => {
      return `${desc.full_start_time} to ${desc.full_end_time}`
    }).join(", ");

    return `Available ${timeString}`
  }, [config, section, catalog])

  const categories = section.categories.map(cat => {
    return <CategoryListing
      category={cat}
      key={cat.keyPath()}
      addToCart={addToCart}
      removeFromCart={removeFromCart}
      updateCart={updateCart}
      availabilityLabel={useSectionTitles ? undefined : availabilityLabel}
    />;
  });

  const sectionHeading = <section>
    <div className="container">
      <div className="catalog-section-heading">
        <h2>{section.title}</h2>
        <p className="is-tiny-muted-text">{section.description}</p>
        <p className="is-tiny-muted-text">{availabilityLabel}</p>
      </div>
    </div>
  </section>;

  return (
    <>
      {useSectionTitles && sectionHeading}
      {categories}
    </>
  );
}
